import React, { useEffect, useState } from "react";
import {
    useNotifications,
    Notification,
} from "../contexts/NotificationContext";
import { DotLottieReact } from "@lottiefiles/dotlottie-react";
import { useAuth } from "../contexts/AuthContext";
import { useSessionTracker } from "../hooks/useSessionTracker";

// Define notification type colors
const typeColors = {
    info: "bg-primary hover:bg-primary/90",
    success: "bg-green-500 hover:bg-green-600",
    warning: "bg-yellow-500 hover:bg-yellow-600",
    error: "bg-red-500 hover:bg-red-600",
    default: "bg-primary hover:bg-primary/90",
};

// Notification Counter component for showing notification count
const NotificationCounter: React.FC = () => {
    const { notifications, showNext } = useNotifications();
    // Only count unread notifications
    const unreadCount = notifications.filter(
        (n) =>
            !localStorage.getItem(`read_notification_${n.id}`) &&
            !localStorage.getItem(`do_not_show_${n.id}`)
    ).length;

    if (unreadCount === 0) return null;

    return (
        <button
            onClick={() => showNext()}
            className="fixed bottom-8 left-8 px-4 py-2 bg-primary text-white rounded-full shadow-lg hover:bg-primary/90 transition-all flex items-center space-x-2 z-50 border border-primary/20">
            <span className="w-5 h-5 bg-red-500 rounded-full flex items-center justify-center text-xs font-bold border border-red-600">
                {unreadCount}
            </span>
            <span className="font-medium">View Notifications</span>
        </button>
    );
};

// Main NotificationPopup component
const NotificationPopup: React.FC = () => {
    const {
        currentNotification,
        dismissCurrent,
        notifications,
        showNext,
        markAllAsRead,
    } = useNotifications();
    const [isVisible, setIsVisible] = useState(false);
    const { firebaseUser } = useAuth();
    const { isNewSession } = useSessionTracker();

    // Calculate the actual active notifications that aren't dismissed
    const activeNotificationCount = notifications.filter(
        (n) => !localStorage.getItem(`do_not_show_${n.id}`)
    ).length;

    // Automatically show the first notification on render if there are notifications
    // that haven't been marked as "don't show again"
    useEffect(() => {
        if (firebaseUser && notifications.length > 0 && !currentNotification) {
            // Filter notifications that aren't marked "don't show again"
            const availableNotifications = notifications.filter(
                (n) => !localStorage.getItem(`do_not_show_${n.id}`)
            );

            if (availableNotifications.length > 0) {
                // Slight delay to ensure UI has rendered properly
                const timer = setTimeout(() => {
                    showNext();
                }, 500);

                return () => clearTimeout(timer);
            }
        }
    }, [firebaseUser, notifications, currentNotification, showNext]);

    // Animation timing
    useEffect(() => {
        if (currentNotification) {
            setIsVisible(true);
        } else {
            // Add a slight delay before hiding for better animation
            const timer = setTimeout(() => {
                setIsVisible(false);
            }, 100);
            return () => clearTimeout(timer);
        }
    }, [currentNotification]);

    // Add body scroll lock when notification is visible
    useEffect(() => {
        if (isVisible) {
            // Prevent scrolling when notification is visible
            document.body.classList.add("overflow-hidden");
        } else {
            // Restore scrolling when notification is hidden
            document.body.classList.remove("overflow-hidden");
        }

        // Cleanup function to ensure overflow is restored
        return () => {
            document.body.classList.remove("overflow-hidden");
        };
    }, [isVisible]);

    // If user is not logged in or there's no notification, only render the counter
    if (!firebaseUser || !currentNotification) {
        return <NotificationCounter />;
    }

    // Get color based on notification type
    const bgColor =
        typeColors[currentNotification.type as keyof typeof typeColors] ||
        typeColors.default;

    // Mark a notification as read
    const handleDismiss = () => {
        localStorage.setItem(
            `read_notification_${currentNotification.id}`,
            "true"
        );
        dismissCurrent();
    };

    // Mark a notification as "don't show again"
    const handleDontShowAgain = () => {
        localStorage.setItem(`do_not_show_${currentNotification.id}`, "true");
        dismissCurrent();
    };

    // Handle dismissing all notifications
    const handleDismissAll = () => {
        // Mark all notifications as permanently dismissed
        notifications.forEach((notification) => {
            localStorage.setItem(
                `read_notification_${notification.id}`,
                "true"
            );
            localStorage.setItem(`do_not_show_${notification.id}`, "true");
        });
        markAllAsRead();
    };

    // Calculate if we should show the Next button
    const showNextButton = activeNotificationCount > 1;

    // Get the current notification index (corrected to be 1-based)
    const currentIndex =
        notifications.findIndex((n) => n.id === currentNotification.id) + 1;

    return (
        <>
            <NotificationCounter />

            {/* Backdrop overlay */}
            <div
                className={`fixed inset-0 bg-black/60 backdrop-blur-sm z-50 transition-opacity duration-300 ${
                    isVisible ? "opacity-100" : "opacity-0 pointer-events-none"
                }`}
                onClick={handleDismiss}
            />

            {/* Main notification popup */}
            <div
                className={`fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 max-w-md w-full transform transition-all duration-500 ease-in-out z-50 ${
                    isVisible
                        ? "scale-100 opacity-100"
                        : "scale-95 opacity-0 pointer-events-none"
                }`}>
                <div className="bg-white rounded-lg shadow-xl overflow-hidden border border-gray-200">
                    {/* Notification header */}
                    <div className={`px-5 py-4 text-white ${bgColor}`}>
                        <div className="flex justify-between items-center">
                            <div className="flex items-center space-x-2">
                                {currentNotification.urgent && (
                                    <span className="inline-flex items-center justify-center w-4 h-4 bg-red-500 rounded-full mr-2"></span>
                                )}
                                <h3 className="font-semibold text-lg">
                                    {currentNotification.title}
                                </h3>
                            </div>
                            <div className="flex items-center space-x-1">
                                {activeNotificationCount > 1 && (
                                    <button
                                        onClick={handleDismissAll}
                                        className="text-xs bg-white/20 px-2 py-1 rounded text-white hover:bg-white/30 transition-colors mr-2">
                                        Dismiss All
                                    </button>
                                )}
                                <button
                                    onClick={handleDismiss}
                                    className="text-white hover:text-gray-200 transition-colors duration-200 p-1 rounded-full hover:bg-white/10">
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        width="20"
                                        height="20"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round">
                                        <line
                                            x1="18"
                                            y1="6"
                                            x2="6"
                                            y2="18"></line>
                                        <line
                                            x1="6"
                                            y1="6"
                                            x2="18"
                                            y2="18"></line>
                                    </svg>
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Notification body */}
                    <div className="p-5">
                        <div className="flex flex-col md:flex-row items-center gap-5">
                            {/* Lottie animation */}
                            {currentNotification.lottieUrl && (
                                <div className="w-28 h-28 flex-shrink-0 bg-gray-50 rounded-lg p-2">
                                    <DotLottieReact
                                        src={currentNotification.lottieUrl}
                                        loop
                                        autoplay
                                    />
                                </div>
                            )}

                            {/* Content */}
                            <div className="flex-1">
                                <p className="text-gray-700 text-base leading-relaxed">
                                    {currentNotification.content}
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Notification footer */}
                    <div className="px-5 py-4 bg-gray-50 flex justify-between border-t border-gray-100">
                        <div className="flex items-center space-x-2">
                            <button
                                onClick={handleDontShowAgain}
                                className="px-4 py-2 rounded text-gray-600 bg-gray-200 hover:bg-gray-300 transition-colors text-sm">
                                Don't show again
                            </button>
                            {activeNotificationCount > 0 && (
                                <span className="text-xs text-gray-400">
                                    {currentIndex} of {activeNotificationCount}
                                </span>
                            )}
                        </div>
                        <div className="flex space-x-2">
                            {showNextButton && (
                                <button
                                    onClick={() => showNext()}
                                    className="px-4 py-2 rounded bg-gray-100 text-gray-600 hover:bg-gray-200 transition-colors">
                                    Next
                                </button>
                            )}
                            <button
                                onClick={handleDismiss}
                                className={`px-5 py-2 rounded-md text-white font-medium ${bgColor} shadow-sm`}>
                                Dismiss
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default NotificationPopup;
